import { ReactNode, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { clsx } from 'clsx'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  children?: ReactNode
  footer?: ReactNode
  showClose?: boolean
  closeOnOverlay?: boolean
  size?: 'sm' | 'md' | 'lg'
}

export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  showClose = true,
  closeOnOverlay = true,
  size = 'md',
}: ModalProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [open, handleKeyDown])

  if (!open) return null

  const sizeClasses = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg' }

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div
        className="fixed inset-0 animate-fade-in"
        style={{ background: 'rgba(0,0,0,0.6)' }}
        onClick={closeOnOverlay ? onClose : undefined}
      />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div
          className={clsx(
            'relative w-full rounded-2xl shadow-2xl animate-slide-up',
            sizeClasses[size]
          )}
          style={{ background: '#0C0C1C', border: '1px solid #1A1A38' }}
          onClick={(e) => e.stopPropagation()}
        >
          {showClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 rounded-lg transition-colors"
              style={{ color: '#52525B' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6 6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}

          {(title || description) && (
            <div className="px-6 pt-6 pb-4">
              {title && <h2 className="text-text font-semibold text-lg">{title}</h2>}
              {description && <p className="mt-1.5 text-text-muted text-sm">{description}</p>}
            </div>
          )}

          {children && <div className="px-6 py-4">{children}</div>}

          {footer && (
            <div className="px-6 pb-6 pt-2 flex gap-3 justify-end">{footer}</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
